import { isSamePointExpiresAt } from 'admin/utils/pointExpiry';
import moment from 'moment';
import React from 'react';
import ReactModal from 'react-modal';
import styled from 'styled-components';
import { Button } from '../atoms/Buttons';
import Headings from '../atoms/Headings';
import { localize } from '../atoms/LocalizedMask';
import { CampaignForm } from '../organisms/campaign/EditCampaign';

type Props = {
  isOpen: boolean;
  campaign: CampaignForm | null;
  comparingCampaign?: CampaignForm | null;
  onClose: () => void;
  onConfirm: () => void;
};

const modalStyles = {
  overlay: {
    zIndex: 9999,
  },
  content: {
    padding: 40,
    top: '50%',
    left: '50%',
    right: '20%',
    bottom: '-30%',
    marginRight: '-30%',
    transform: 'translate(-50%, -50%)',
    minWidth: 1258,
  },
};

const ModalHeader = styled(Headings.h4)`
  font-size: 20px;
  text-align: center;
  margin: 0;
  width: 100%;
  font-weight: 700;
`;

const ModalContent = styled.div`
  margin: 60px 0;
  display: flex;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 24px;
`;

const ColumnSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Divider = styled.div`
  border-right: 1px solid #e1e1e1;
  margin: 0 60px;
`;

const ColumnHeader = styled(Headings.h5)`
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  margin-bottom: 8px;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionHeader = styled(Headings.h6)`
  display: flex;
  gap: 24px;
  color: #9f9f9f;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  margin: 0;
`;

const SectionLabel = styled.label`
  width: 200px;
  color: #9f9f9f;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  margin: 0;
  display: flex;
  column-gap: 12px;
  row-gap: 8px;
  align-items: flex-start;
  flex-wrap: wrap;
  align-content: flex-start;
`;

const InfoText = styled.p<{ isBold?: boolean }>`
  margin: 0;
  color: #222;
  font-size: 16px;
  font-style: normal;
  font-weight: ${({ isBold = false }) => (isBold ? '700' : '400')};
  line-height: 24px;
  flex: 1;
`;

const InfoTag = styled.span`
  margin: 0;
  color: #5c5c5c;
  font-size: 16px;
  font-style: normal;
  line-height: 24px;
`;

const InfoScrollable = styled.div<{ maxHeight?: number }>`
  flex: 1;
  max-height: ${({ maxHeight = 180 }) => `${maxHeight}px`};
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
`;

const SubSection = styled.section`
  display: flex;
  gap: 24px;
  > ${SectionLabel} {
    padding-left: 20px;
    font-weight: 400;
  }
`;

const PointRule = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border: 1px solid #e1e1e1;
  border-radius: 4px;
`;

const PointRuleValueRow = styled.div`
  display: flex;
  gap: 24px;
`;

const PointRuleLabel = styled(SectionLabel)`
  width: 80px;
`;

const ProductBasedPointRuleLable = styled(SectionLabel)`
  min-width: 80px;
  max-width: 160px;
`;

const EditedBadgeContainer = styled.span`
  border-radius: 4px;
  background: #21bf25;
  width: 64px;
  color: #fff;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  display: inline-block;
`;

const CAMPAIGN_TYPES = [
  { label: '支払い', value: 'payment' },
  { label: 'チャージ', value: 'topup' },
  { label: 'ポケペイ外決済', value: 'external-transaction' },
];

const CHARGING_METHODS = [
  { label: 'クレジットカード', value: 'creditcard' },
  { label: '銀行振込', value: 'bankpay' },
  { label: 'セブン銀行ATM', value: 'sevenbank-atm' },
];

const DAYS = [
  { label: '月', value: 0 },
  { label: '火', value: 1 },
  { label: '水', value: 2 },
  { label: '木', value: 3 },
  { label: '金', value: 4 },
  { label: '土', value: 5 },
  { label: '日', value: 6 },
];

const POINT_RULES_TYPES = [
  {
    label: '取引総額',
    value: 'allAmountUniform',
  },
  {
    label: '取引総額（金額レンジごと）',
    value: 'allAmountCustom',
  },
  {
    label: '取引マネー金額',
    value: 'moneyAmountUniform',
  },
  {
    label: '取引マネー金額（金額レンジごと）',
    value: 'moneyAmountCustom',
  },
  {
    label: '商品ごと',
    value: 'product',
  },
] as const;

const EditedBadge = () => <EditedBadgeContainer>変更あり</EditedBadgeContainer>;

const SAME_CAMPAIGN_COMPARISON = {
  name: true,
  description: true,
  type: true,
  typeTransaction: true,
  priority: true,
  isExclusive: true,
  shopSpecification: true,
  applicableShops: true,
  applicableShopLabels: true,
  dateRange: true,
  daysOfWeek: true,
  timeRanges: true,
  ruleType: true,
  rules: true,
  productMinNumberForCombinationPurchase: true,
  maxPointTotalPointAmount: true,
  maxPointAmount: true,
  budgetCapsAmount: true,
  pointExpiresAt: true,
  pointExpiresInDays: true,
  residentCheck: true,
};

type CampaignComparisonModal = typeof SAME_CAMPAIGN_COMPARISON;

const shopSpecificationLabel = (spec: CampaignForm['shopSpecification']) => {
  if (spec === 'whitelist') return '許可リスト';
  if (spec === 'labels') return '許可リスト(店舗ラベル)';
  if (spec === 'blacklist') return '除外リスト';
  return null;
};

const compareArray = <T,>(arr1: T[], arr2: T[], comparer: (x1: T, x2: T) => boolean) =>
  arr1.length === arr2.length && arr1.every((value, index) => comparer(value, arr2[index]));

const CampaignModal = ({ isOpen, campaign, comparingCampaign, onClose, onConfirm }: Props) => {
  if (!campaign) return null;

  const compareRules = (ruleType: typeof campaign.selectedPointRulesType) => {
    if (ruleType === 'allAmountUniform' || ruleType === 'moneyAmountUniform') {
      const rule = campaign.amountBasedPointRules.uniformAmountRule;
      const comparingRule = comparingCampaign?.amountBasedPointRules.uniformAmountRule;
      return rule.unit === comparingRule?.unit && rule.amount === comparingRule.amount;
    }

    if (ruleType === 'allAmountCustom' || ruleType === 'moneyAmountCustom') {
      const rules = campaign.amountBasedPointRules.customAmountRules;
      const comparingRules = comparingCampaign?.amountBasedPointRules.customAmountRules;
      return compareArray(
        rules,
        comparingRules ?? [],
        (x, y) => x.amount === y.amount && x.unit === y.unit && x.from === y.from && x.to === y.to
      );
    }

    const rules = campaign.productBasedPointRules.productBasedPointRules;
    const comparingRules = comparingCampaign?.productBasedPointRules.productBasedPointRules;

    return compareArray(
      rules,
      comparingRules ?? [],
      (x, y) =>
        x.product_code === y.product_code &&
        x.point_amount === y.point_amount &&
        x.point_amount_unit === y.point_amount_unit
    );
  };

  const campaignComparison: CampaignComparisonModal = !comparingCampaign
    ? SAME_CAMPAIGN_COMPARISON
    : {
        name: campaign.name === comparingCampaign.name,
        description: campaign.description === comparingCampaign.description,
        type: campaign.event === comparingCampaign.event,
        typeTransaction:
          campaign.applicableTransactionMetadata ===
          comparingCampaign.applicableTransactionMetadata,
        priority: campaign.priority === comparingCampaign.priority,
        isExclusive: campaign.isExclusive === comparingCampaign.isExclusive,
        shopSpecification: campaign.shopSpecification === comparingCampaign.shopSpecification,
        applicableShops: compareArray(
          campaign.applicableShops,
          comparingCampaign.applicableShops,
          (x, y) => x.id === y.id
        ),
        applicableShopLabels: compareArray(
          campaign.applicableShopLabels.flatMap((r) => r.itemIds),
          comparingCampaign.applicableShopLabels.flatMap((r) => r.itemIds),
          (x, y) => x === y
        ),
        dateRange:
          (campaign.dateRange?.from?.isSame(comparingCampaign.dateRange?.from) ?? true) &&
          (campaign.dateRange?.to?.isSame(comparingCampaign.dateRange?.to) ?? true),
        daysOfWeek: compareArray(
          campaign.daysOfWeek,
          comparingCampaign.daysOfWeek,
          (x, y) => x === y
        ),
        timeRanges: compareArray(
          campaign.timeRanges,
          comparingCampaign.timeRanges,
          (x, y) => x.from === y.from && x.to === y.to
        ),
        ruleType: campaign.selectedPointRulesType === comparingCampaign.selectedPointRulesType,
        rules:
          campaign.selectedPointRulesType === comparingCampaign.selectedPointRulesType &&
          compareRules(campaign.selectedPointRulesType),
        productMinNumberForCombinationPurchase:
          campaign.selectedPointRulesType !== 'product' ||
          campaign.productBasedPointRules.miniumNumberForCombinationPurchase ===
            comparingCampaign.productBasedPointRules.miniumNumberForCombinationPurchase,
        maxPointTotalPointAmount:
          campaign.maxTotalPointAmount === comparingCampaign.maxTotalPointAmount,
        maxPointAmount: campaign.maxPointAmount === comparingCampaign.maxPointAmount,
        budgetCapsAmount: campaign.budgetCapsAmount === comparingCampaign.budgetCapsAmount,
        pointExpiresAt: isSamePointExpiresAt(
          campaign.pointExpiresAt,
          comparingCampaign.pointExpiresAt
        ),
        pointExpiresInDays: campaign.pointExpiresInDays === comparingCampaign.pointExpiresInDays,
        residentCheck: campaign.residentCheck === comparingCampaign.residentCheck,
      };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick
      closeTimeoutMS={200}
      ariaHideApp={false}
      style={modalStyles}
    >
      <ModalHeader>
        {comparingCampaign
          ? '以下の内容でキャンペーンを更新します'
          : '以下の内容でキャンペーンを登録します'}
      </ModalHeader>
      <ModalContent>
        <ColumnSection>
          <ColumnHeader>基本設定</ColumnHeader>

          <Section>
            <SectionHeader>
              <SectionLabel>マネー</SectionLabel>
              <InfoText>{campaign.privateMoney.name}</InfoText>
            </SectionHeader>
          </Section>

          <Section>
            <SectionHeader>ポイント負担先</SectionHeader>

            <SubSection>
              <SectionLabel>組織</SectionLabel>
              <InfoText>{campaign.organization?.name}</InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>店舗</SectionLabel>
              <InfoText>{campaign.pointBearerShop?.name}</InfoText>
            </SubSection>
          </Section>

          <Section>
            <SectionHeader>キャンペーン</SectionHeader>

            <SubSection>
              <SectionLabel>
                キャンペーン名 {!campaignComparison.name && <EditedBadge />}
              </SectionLabel>
              <InfoText isBold={!campaignComparison.name}>{campaign.name}</InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>説明 {!campaignComparison.description && <EditedBadge />}</SectionLabel>
              <InfoText isBold={!campaignComparison.description}>
                {campaign.description || '設定なし'}
              </InfoText>
            </SubSection>
          </Section>

          <Section>
            <SectionHeader>対象取引</SectionHeader>

            <SubSection>
              <SectionLabel>取引 {!campaignComparison.type && <EditedBadge />}</SectionLabel>
              <InfoText isBold={!campaignComparison.type}>
                {CAMPAIGN_TYPES.find((v) => v.value === campaign.event)?.label || ''}
              </InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>
                取引条件 {!campaignComparison.typeTransaction && <EditedBadge />}
              </SectionLabel>
              <InfoText isBold={!campaignComparison.typeTransaction}>
                {CHARGING_METHODS.find((v) => v.value === campaign.applicableTransactionMetadata)
                  ?.label || '設定なし'}
              </InfoText>
            </SubSection>
          </Section>

          <Section>
            <SectionHeader>順序付け</SectionHeader>

            <SubSection>
              <SectionLabel>優先度 {!campaignComparison.priority && <EditedBadge />}</SectionLabel>
              <InfoText isBold={!campaignComparison.priority}>{campaign.priority}</InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>
                重ね掛け {!campaignComparison.isExclusive && <EditedBadge />}
              </SectionLabel>
              <InfoText isBold={!campaignComparison.isExclusive}>
                {!campaign.isExclusive ? '有効' : '無効'}
              </InfoText>
            </SubSection>
          </Section>

          <Section>
            <SectionHeader>対象</SectionHeader>

            <SubSection>
              <SectionLabel>
                店舗
                {shopSpecificationLabel(campaign.shopSpecification) && (
                  <InfoTag>{shopSpecificationLabel(campaign.shopSpecification)}</InfoTag>
                )}{' '}
                {(!campaignComparison.shopSpecification ||
                  !campaignComparison.applicableShops ||
                  !campaignComparison.applicableShopLabels) && <EditedBadge />}
              </SectionLabel>
              <InfoScrollable>
                {campaign.shopSpecification === 'labels' &&
                  campaign.applicableShopLabels
                    .filter((row) => row.itemIds.length > 0)
                    .map((row) => (
                      <InfoText
                        key={row.groupId ?? ''}
                        isBold={
                          !campaignComparison.shopSpecification ||
                          !campaignComparison.applicableShopLabels
                        }
                      >
                        {row.groupName}: {row.itemNames.join('、')}
                      </InfoText>
                    ))}
                {campaign.shopSpecification !== 'labels' &&
                  (campaign.shopSpecification === 'none' ||
                  (campaign.applicableShops?.length ?? 0) === 0 ? (
                    <InfoText
                      isBold={
                        !campaignComparison?.shopSpecification ||
                        !campaignComparison.applicableShops
                      }
                    >
                      指定なし
                    </InfoText>
                  ) : (
                    campaign.applicableShops.map((shop, index) => (
                      <InfoText
                        isBold={
                          !campaignComparison?.shopSpecification ||
                          (!campaignComparison.applicableShops &&
                            (index >= (comparingCampaign?.applicableShops.length ?? 0) ||
                              shop.id !== comparingCampaign?.applicableShops[index].id))
                        }
                      >
                        {shop.name}
                      </InfoText>
                    ))
                  ))}
              </InfoScrollable>
            </SubSection>

            <SubSection>
              <SectionLabel>期間 {!campaignComparison.dateRange && <EditedBadge />}</SectionLabel>
              <InfoText isBold={!campaignComparison.dateRange}>
                {campaign.dateRange?.from?.format('YYYY/MM/DD HH:mm')} ～{' '}
                {campaign.dateRange?.to?.format('YYYY/MM/DD HH:mm')}
              </InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>曜日 {!campaignComparison.daysOfWeek && <EditedBadge />}</SectionLabel>
              <InfoText isBold={!campaignComparison.daysOfWeek}>
                {campaign.daysOfWeek.length === 0
                  ? '設定なし'
                  : campaign.daysOfWeek
                      .map((x) => DAYS.find((d) => d.value === x)?.label)
                      .join(', ')}
              </InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>
                時間帯 {!campaignComparison.timeRanges && <EditedBadge />}
              </SectionLabel>
              <InfoScrollable>
                {campaign.timeRanges.length === 0 ? (
                  <InfoText isBold={!campaignComparison.timeRanges}>設定なし</InfoText>
                ) : (
                  campaign.timeRanges.map((range, index) => (
                    <InfoText
                      isBold={
                        !campaignComparison.timeRanges &&
                        (index >= (comparingCampaign?.timeRanges.length ?? 0) ||
                          range.from !== comparingCampaign?.timeRanges[index].from ||
                          range.to !== comparingCampaign?.timeRanges[index].to)
                      }
                    >
                      {range.from} ~ {range.to}
                    </InfoText>
                  ))
                )}
              </InfoScrollable>
            </SubSection>

            <SubSection>
              <SectionLabel>
                対象会員 {!campaignComparison.residentCheck && <EditedBadge />}
              </SectionLabel>
              <InfoText isBold={!campaignComparison.residentCheck}>
                {campaign.residentCheck === 'none'
                  ? '指定なし'
                  : campaign.residentCheck === 'required'
                  ? '住民認証あり'
                  : '住民認証なし'}
              </InfoText>
            </SubSection>
          </Section>
        </ColumnSection>

        <Divider />

        <ColumnSection>
          <ColumnHeader>詳細設定</ColumnHeader>

          <Section>
            <SectionHeader>
              <SectionLabel>付与値</SectionLabel>
              <InfoText>
                {campaign.selectedPointRulesType !== 'product' ? '取引額ベース' : '商品ごと'}
              </InfoText>
            </SectionHeader>
          </Section>

          <Section>
            <SectionHeader>ポイント還元</SectionHeader>

            <SubSection>
              <SectionLabel>
                還元対象 {!campaignComparison.ruleType && <EditedBadge />}
              </SectionLabel>
              <InfoText isBold={!campaignComparison.ruleType}>
                {POINT_RULES_TYPES.find((type) => type.value === campaign.selectedPointRulesType)
                  ?.label ?? ''}
              </InfoText>
            </SubSection>

            {campaign.selectedPointRulesType === 'product' && (
              <SubSection>
                <SectionLabel>
                  複数種類の商品を同時購入するときの商品種別数の下限{' '}
                  {!campaignComparison.productMinNumberForCombinationPurchase && <EditedBadge />}
                </SectionLabel>
                <InfoText isBold={!campaignComparison.productMinNumberForCombinationPurchase}>
                  {campaign.productBasedPointRules.miniumNumberForCombinationPurchase || '設定なし'}
                </InfoText>
              </SubSection>
            )}

            <SubSection>
              <SectionLabel>還元設定 {!campaignComparison.rules && <EditedBadge />}</SectionLabel>
              <InfoScrollable maxHeight={380}>
                {['allAmountUniform', 'moneyAmountUniform'].includes(
                  campaign.selectedPointRulesType
                ) && (
                  <PointRule>
                    <PointRuleValueRow>
                      <PointRuleLabel>付与タイプ</PointRuleLabel>
                      <InfoText
                        isBold={
                          !campaignComparison.rules &&
                          campaign.amountBasedPointRules.uniformAmountRule.unit !==
                            comparingCampaign?.amountBasedPointRules.uniformAmountRule.unit
                        }
                      >
                        {campaign.amountBasedPointRules.uniformAmountRule.unit === 'percent'
                          ? '%'
                          : '絶対'}
                      </InfoText>
                    </PointRuleValueRow>
                    <PointRuleValueRow>
                      <PointRuleLabel>値</PointRuleLabel>
                      <InfoText
                        isBold={
                          !campaignComparison.rules &&
                          campaign.amountBasedPointRules.uniformAmountRule.amount !==
                            comparingCampaign?.amountBasedPointRules.uniformAmountRule.amount
                        }
                      >
                        {campaign.amountBasedPointRules.uniformAmountRule.unit === 'percent'
                          ? campaign.amountBasedPointRules.uniformAmountRule.amount
                          : localize(
                              campaign.amountBasedPointRules.uniformAmountRule.amount?.toString() ??
                                ''
                            )}
                      </InfoText>
                    </PointRuleValueRow>
                  </PointRule>
                )}
                {['allAmountCustom', 'moneyAmountCustom'].includes(
                  campaign.selectedPointRulesType
                ) &&
                  campaign.amountBasedPointRules.customAmountRules.map((rule, index) => (
                    <PointRule>
                      <PointRuleValueRow>
                        <PointRuleLabel>取引金額</PointRuleLabel>
                        <InfoText
                          isBold={
                            !campaignComparison.rules &&
                            `${rule.from}${rule.to}` !==
                              `${comparingCampaign?.amountBasedPointRules.customAmountRules[index]?.from}${campaign.amountBasedPointRules.customAmountRules[index]?.to}`
                          }
                        >
                          {localize(rule.from?.toString() ?? '')} →{' '}
                          {localize(rule.to?.toString() ?? '')}
                        </InfoText>
                      </PointRuleValueRow>
                      <PointRuleValueRow>
                        <PointRuleLabel>付与タイプ</PointRuleLabel>
                        <InfoText
                          isBold={
                            !campaignComparison.rules &&
                            rule.unit !==
                              comparingCampaign?.amountBasedPointRules.customAmountRules[index]
                                ?.unit
                          }
                        >
                          {rule.unit === 'percent' ? '%' : '絶対'}
                        </InfoText>
                      </PointRuleValueRow>
                      <PointRuleValueRow>
                        <PointRuleLabel>値</PointRuleLabel>
                        <InfoText
                          isBold={
                            !campaignComparison.rules &&
                            rule.amount?.toString() !==
                              comparingCampaign?.amountBasedPointRules.customAmountRules[
                                index
                              ]?.amount?.toString()
                          }
                        >
                          {rule.unit === 'percent'
                            ? rule.amount
                            : localize(rule.amount?.toString() ?? '')}
                        </InfoText>
                      </PointRuleValueRow>
                    </PointRule>
                  ))}
                {campaign.selectedPointRulesType === 'product' &&
                  campaign.productBasedPointRules.productBasedPointRules.map((rule, index) => (
                    <PointRule>
                      <PointRuleValueRow>
                        <ProductBasedPointRuleLable>対象商品コード</ProductBasedPointRuleLable>
                        <InfoText
                          isBold={
                            !campaignComparison.rules &&
                            rule.product_code !==
                              comparingCampaign?.productBasedPointRules.productBasedPointRules[
                                index
                              ]?.product_code
                          }
                        >
                          {rule.product_code}
                        </InfoText>
                      </PointRuleValueRow>
                      <PointRuleValueRow>
                        <ProductBasedPointRuleLable>ポイント額</ProductBasedPointRuleLable>
                        <InfoText
                          isBold={
                            !campaignComparison.rules &&
                            rule.point_amount !==
                              comparingCampaign?.productBasedPointRules.productBasedPointRules[
                                index
                              ]?.point_amount
                          }
                        >
                          {rule.point_amount_unit === 'percent'
                            ? rule.point_amount
                            : localize(rule.point_amount.toString() ?? '')}
                        </InfoText>
                      </PointRuleValueRow>
                      <PointRuleValueRow>
                        <ProductBasedPointRuleLable>単位</ProductBasedPointRuleLable>
                        <InfoText
                          isBold={
                            !campaignComparison.rules &&
                            rule.point_amount_unit !==
                              comparingCampaign?.productBasedPointRules.productBasedPointRules[
                                index
                              ]?.point_amount_unit
                          }
                        >
                          {rule.point_amount_unit === 'percent' ? '%' : '絶対'}
                        </InfoText>
                      </PointRuleValueRow>
                      {!!rule.starts_at && (
                        <PointRuleValueRow>
                          <ProductBasedPointRuleLable>開始時間</ProductBasedPointRuleLable>
                          <InfoText>{moment(rule.starts_at).format('YYYY/MM/DD HH:mm')}</InfoText>
                        </PointRuleValueRow>
                      )}
                      {!!rule.ends_at && (
                        <PointRuleValueRow>
                          <ProductBasedPointRuleLable>終了時間</ProductBasedPointRuleLable>
                          <InfoText>{moment(rule.ends_at).format('YYYY/MM/DD HH:mm')}</InfoText>
                        </PointRuleValueRow>
                      )}
                      {!!rule.required_count && (
                        <PointRuleValueRow>
                          <ProductBasedPointRuleLable>運用対象購入個数</ProductBasedPointRuleLable>
                          <InfoText
                            isBold={
                              !campaignComparison.rules &&
                              rule.required_count !==
                                comparingCampaign?.productBasedPointRules.productBasedPointRules[
                                  index
                                ]?.required_count
                            }
                          >
                            {rule.required_count}
                          </InfoText>
                        </PointRuleValueRow>
                      )}
                      <PointRuleValueRow>
                        <ProductBasedPointRuleLable>
                          複数購入したときにポイント付与額に個数を掛ける
                        </ProductBasedPointRuleLable>
                        <InfoText
                          isBold={
                            !campaignComparison.rules &&
                            rule.is_multiply_by_count !==
                              comparingCampaign?.productBasedPointRules.productBasedPointRules[
                                index
                              ]?.is_multiply_by_count
                          }
                        >
                          {rule.is_multiply_by_count ? 'はい' : 'いいえ'}
                        </InfoText>
                      </PointRuleValueRow>
                    </PointRule>
                  ))}
              </InfoScrollable>
            </SubSection>
          </Section>

          <Section>
            <SectionHeader>ポイント還元の上限金額</SectionHeader>

            <SubSection>
              <SectionLabel>
                1ユーザーあたりの累計額{' '}
                {!campaignComparison.maxPointTotalPointAmount && <EditedBadge />}
              </SectionLabel>
              <InfoText isBold={!campaignComparison.maxPointTotalPointAmount}>
                {localize(campaign.maxTotalPointAmount?.toString() ?? '') || '設定なし'}
              </InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>
                1取引あたりの金額 {!campaignComparison.maxPointAmount && <EditedBadge />}
              </SectionLabel>
              <InfoText isBold={!campaignComparison.maxPointAmount}>
                {localize(campaign.maxPointAmount?.toString() ?? '') || '設定なし'}
              </InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>
                1キャンペーンあたりの金額 {!campaignComparison.budgetCapsAmount && <EditedBadge />}
              </SectionLabel>
              <InfoText isBold={!campaignComparison.budgetCapsAmount}>
                {localize(campaign.budgetCapsAmount?.toString() ?? '') || '設定なし'}
              </InfoText>
            </SubSection>
          </Section>

          <Section>
            <SectionHeader>ポイント有効期限</SectionHeader>

            <SubSection>
              <SectionLabel>
                日時指定 {!campaignComparison.pointExpiresAt && <EditedBadge />}
              </SectionLabel>
              <InfoText isBold={!campaignComparison.pointExpiresAt}>
                {campaign.pointExpiresAt?.format('YYYY/MM/DD HH:mm') || '設定なし'}
              </InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>
                相対日数指定 {!campaignComparison.pointExpiresInDays && <EditedBadge />}
              </SectionLabel>
              <InfoText isBold={!campaignComparison.pointExpiresInDays}>
                {campaign.pointExpiresInDays || '設定なし'}
              </InfoText>
            </SubSection>
          </Section>
        </ColumnSection>
      </ModalContent>

      <ModalFooter>
        <Button variant="neutral" onClick={onClose}>
          キャンセル
        </Button>
        <Button variant="contained" onClick={onConfirm}>
          {comparingCampaign ? '更新' : '登録'}
        </Button>
      </ModalFooter>
    </ReactModal>
  );
};

export default CampaignModal;
